import Adapter from './Api/Adapter.es6';

(() => {
    class Api {
        constructor() {
            /* Supported Request Methods. */
            this.METHOD_PUT         = 'put';
            this.METHOD_GET         = 'get';
            this.METHOD_POST        = 'post';
            this.METHOD_DELETE      = 'delete';

            /* Configuration */
            this.Adapter            = new Adapter();
            this.Storage            = new App.Interface.Storage('api');
            this.Storage.setValidation('session', false);

            /* Vars */
            this.requests           = {}; // Active requests (Pending list)
            this.async              = true;

            /* Globalize Resource */
            App.Api = endpoint => this.load(endpoint, {}, false, false);
        }

        prepareModel(parameters, endpoint, options, method, payload) {
            const request     = new App.Entities.Api();
            request.id          = parameters.id || null;    // define an ID for single request
            request.parameters  = parameters;
            request.endpoint    = endpoint;                 // define APIs endpoint
            request.method      = method;                   // define requests method

            // Add defined option if available
            for (let key in options) request[key] = options[key];

            /* Set POST Data to submit */
            if ((method === this.METHOD_POST || method === this.METHOD_PUT) && payload !== null) {
                switch(request.contentType) {
                    case request.CONTENT_TYPE_JSON:
                        request.data        = JSON.stringify(payload);
                        request.processData = false;
                        break;
                    default:
                        request.data        = payload;
                        break;
                }
            }
            return request;
        }

        fetchDataFromView(request) {
            const data = App.Data.getApi(request.endpoint);
            return data?.data || data || undefined;
        }

        fetchDataFromCache(request) {
            let data = this.Storage.get(request.endpoint);
            return data?.data || data || undefined;
        }


        /**
         * Return data if we already load or preload it
         */
        fetchPreservedData(request) {
            /* We don't load API ONCE if view is offering API in advance coming from VIEW */
            if (request.method === this.METHOD_GET) {
                const response = this.fetchDataFromView(request);
                if (response)
                    return response.data || response;
            }
            // /* Stop request if already have the cache */
            if (request.method === this.METHOD_GET && request.cache) {
                const response = this.fetchDataFromCache(request);
                if (response)
                    return response.data || response;
            }
        }

        /**
         * Executes an API call. This is a complete wrapper around mymuesli API that covers
         * all request methods, additional headers and request parameters, JSON payloads in the body
         * and success/error handling via callbacks.
         *
         * In order to avoid implementation errors and trouble this should be used instead of native
         * jQuery ajax requests. E.g. it covers already the usage of API keys and how to encode a JSON
         * objects as file upload the right way.
         *
         * Methods:
         * put / get / post
         *
         * Endpoints:
         * The name of the endpoint in dot-notation, e.g. Analytics.Google.Events
         *
         * Parameters:
         * Additional request parameters like the id.
         * - id: The id of an object that's retrieved in a view action.
         *       If this parameter is set it will be a view instead of a list request.
         * - ...:
         *
         * Payload:
         * The payload as object. It will be encoded as POST and submitted in the request body of the call.
         *
         * Options:
         * - key: The API key that should be used in the request. Usually the default API key for ES6+Locale is used.
         *
         * Callbacks:
         * The callbacks to handle the request-results.
         *
         * @param method string The request method.
         * @param endpoint string The name of the end-point.
         * @param parameters object Additional request parameters.
         * @param payload object The json of the payload that should be included in the request body.
         * @param options object Additional options.
         * @param onSuccess function The callback for successful api requests.
         * @param onError function The callback for unsuccessful api requests.
         */
        call (method, endpoint, parameters = {}, payload = null, options = {}, onSuccess = function () {}, onError = function () {}) {
            const request = this.prepareModel(parameters, endpoint, options, method, payload);
            const response = this.fetchPreservedData(request);
            if (response) {
                onSuccess(response);
                return response; // we stop here
            }

            /* Do not make same API-Calls if there is a pending call */
            if (request.method === this.METHOD_GET && this.requests[request.endpoint] && request.async) {
                if (request.cache) {
                    this.wait(request.endpoint, () => {
                        const response = this.fetchDataFromCache(request);
                        if (response) onSuccess(response);
                    });
                }
                return; // we stop here
            }

            let ajax = request.objectify();
            ajax.url = request.url; // todo: get the super.url while objectify
            /* Make the API-call. */
            if (request.method === this.METHOD_GET) this.addEndpointFromPendingList(request.endpoint);
            $.ajax(ajax).done(response => {
                // use DATA if response uses metadata
                request.response  = response.data || response;
                // Save to cache only if allowed
                request.cache = (response.cache !== undefined) ? response.cache : request.cache;
                // Save Storage response only if is GET method
                if (request.method === this.METHOD_GET && request.cache) {
                    this.Storage.set(request.endpoint, request.response);
                }
                this.success(request, onSuccess);
            }).fail(response => {
                request.response  = response;
                this.error(request, onError);
            });
            return this.Storage.get(request.endpoint) || request.response;
        }

        /**
         * Behavior when API is done
         *
         * @param request       Entire request information
         * @param callback
         */
        success(request, callback) {
            if (request.response.notifications !== undefined)
                App.Interface.Notification.setFromBundle(request.response.notifications);
            callback(request.response);
            this.removeEndpointFromPendingList(request.endpoint);
        }

        /**
         * Behavior when API fails
         *
         * @param request      Response from API call
         * @param callback
         */
        error(request, callback) {
            let Notification =  App.Interface.Notification;
            switch (request.response.status) {
                case 400:
                    let json = request.response.responseJSON;
                    if (json.notifications !== undefined)
                        Notification.setFromBundle(json.notifications);
                    break;
                case 403:
                    Notification.add('floating', request.response.responseJSON.message).update();
                    break;
                case 404:
                    Notification.add('floating', 'API "' + request.endpoint + '" ' + request.response.statusText).update();
                    break;
            }
            const response = request.response.responseJSON;
            if (response) {
                callback(response);
            } else {
                // Sometimes the API fails to give a response
                console.error('API Call Error', request);
            }
            this.removeEndpointFromPendingList(request.endpoint);
        }

        /**
         * add endpoint from pending list
         */
        addEndpointFromPendingList(endpoint) {
            this.requests[endpoint] = true;
        }

        /**
         * Remove endpoint from pending list
         */
        removeEndpointFromPendingList(endpoint) {
            this.requests[endpoint] = false;
        }

        /**
         * Executes a GET request on the API in an efficient way from Frontend
         *
         * @see call()-Method
         *
         * @param endpoint      String The name of the end-point.
         * @param onSuccess     Function The callback for successful api requests.
         * @param cache         Endpoint will be renewed in Storage every call
         * @param async         Is async
         */
        load(endpoint, onSuccess, cache = true, async = true) {
            let options = {
                cache:      cache,
                async:      async,
            };
            if (typeof onSuccess !== 'function') onSuccess = function() {};
            return this.get(endpoint, {}, options, onSuccess, function() {
                console.log('API ERROR');
            });
        }

        /**
         * Executes a GET request on the API.
         *
         * @see call()-Method
         *
         * @param endpoint string The name of the end-point.
         * @param parameters object Additional request parameters.
         * @param options object Additional options.
         * @param onSuccess function The callback for successful api requests.
         * @param onError function The callback for unsuccessful api requests.
         */
        get(endpoint, parameters, options, onSuccess, onError) {
            return this.call(this.METHOD_GET, endpoint, parameters, null, options, onSuccess, onError);
        }

        /**
         * Executes a POST request on the API.
         *
         * @see call()-Method
         *
         * @param endpoint string The name of the end-point.
         * @param parameters object Additional request parameters.
         * @param payload object The json of the payload that should be included in the request body.
         * @param options object Additional options.
         * @param onSuccess function The callback for successful api requests.
         * @param onError function The callback for unsuccessful api requests.
         */
        post (endpoint, parameters, payload, options, onSuccess, onError) {
            return this.call(this.METHOD_POST, endpoint, parameters, payload, options, onSuccess, onError);
        }

        /**
         * Executes a DELETE request on the API.
         *
         * @see call()-Method
         *
         * @param endpoint string The name of the end-point.
         * @param parameters object Additional request parameters.
         * @param options object Additional options.
         * @param onSuccess function The callback for successful api requests.
         * @param onError function The callback for unsuccessful api requests.
         */
        remove (endpoint, parameters, options, onSuccess, onError) {
            return this.call(this.METHOD_DELETE, endpoint, parameters, null, options, onSuccess, onError);
        }

        /**
         * Wait for endpoint to be available
         *
         * @param endpoint
         * @param callback
         * @param comparison (undefined, null, empty, etc)
         */
        wait(endpoint, callback, comparison = undefined) {
            App.wait('App.Interface.Api.Storage.data.' + endpoint.toLowerCase(), callback, comparison);
        }

        /**
         * Find item by ID from Endpoint Collection
         *
         * @param endpoint
         * @param id
         * @param callback
         */
        findById(endpoint, id, callback) {
            App.Interface.Api.load(endpoint, items => {
                let item = items.find(element => (element.id == id));
                if (item !== undefined) {
                    callback(item);
                } else {
                    this.findOneById(endpoint, id, callback);
                }
            });
        }

        /**
         * Call Endpoint with ID
         *
         * @param endpoint
         * @param id
         * @param callback
         */
        findOneById(endpoint, id, callback) {
            App.Interface.Api.load(endpoint + '/' + id, function(item) {
                if (item !== undefined) callback(item);
            });
        }

        /**
         * Merge API Endpoints
         *
         * Deliveries two or more endpoints together.
         *
         * @param endpoints Array with endpoints to merge
         * @param callback
         */
        merge(endpoints = [], callback) {
            let Self    = this;
            let wait    = 0;

            endpoints.forEach((endpoint) => Self.load(endpoint));                   /* Load endpoints */
            endpoints.forEach((endpoint) => Self.wait(endpoint, () => wait++));     /* Wait for endpoints to be loaded */

            /* Wait contains the number of endpoint already loaded */
            let interval = setInterval(() => {
                /* The number of finally loaded endpoints hast to be the same as the number of requested endpoints */
                if (wait >= endpoints.length) {
                    let merge = [];
                    endpoints.forEach((endpoint) => Self.load(endpoint, (response) => {
                        response.forEach((item) => merge.push(item));
                    }));
                    clearInterval(interval);
                    try {
                        callback(merge);
                    } catch (e) {
                        clearInterval(interval);
                        throw new Error(e)
                    }
                }
            });
            setTimeout(() => {
                clearInterval(interval);
                App.error('Error merging endpoints:' + endpoints.join());
            }, 5000);
        }

        promise(endpoint, params, options = {}) {
            return new Promise((resolve, reject) => {
                if (options['method'] === this.METHOD_POST) {
                    this.post(
                        endpoint,
                        {},
                        params,
                        options,
                        response => resolve(response),
                        response => reject(response)
                    );
                } else {
                    if (this.requests[endpoint]) {
                        // Check if other request is already in march and wait for that request to finish
                        this.wait(endpoint, () => resolve(this.Storage.get(endpoint)));
                    } else {
                        this.get(
                            endpoint,
                            params,
                            options,
                            response => resolve(response),
                            response => reject(response)
                        );
                    }
                }
            });
        }
    }
    App.Interface.Api = new Api();
})();

